<template>
    <div class="row">
        <div class="col-8">
            <div class="d-flex my-2">
                <div class="mr-2">
                    <input
                        type="text"
                        name="name"
                        placeholder="Search"
                        class="form-control"
                        v-model="searchName"
                        @input="debouncedSearch"
                        data-test="search-input"
                    />
                </div>
            </div>
            <div class="card card-gg mb-1">
                <table class="table table-a">
                    <thead>
                    <tr>
                        <th class="checkbox-column">
                            <div class="custom-control custom-checkbox">
                                <input
                                    v-model="selectAll"
                                    @change="toggleSelectAll"
                                    type="checkbox"
                                    name="tableSelectAll"
                                    autocomplete="off"
                                    class="custom-control-input"
                                    id="tableSelectAll"
                                    data-test="select-all-checkbox"
                                />
                                <label class="custom-control-label" for="tableSelectAll"></label>
                            </div>
                        </th>
                        <th>
                            <button
                                @click.prevent="sortBy('name')"
                                class="d-flex align-content-center align-items-center justify-content-between"
                                data-test="sort-by-name"
                            >
                                Asset Name
                                <i :class="getSortIconClass('name')"></i>
                            </button>
                        </th>
                        <th>
                            <button
                                @click.prevent="sortBy('category')"
                                class="d-flex align-content-center align-items-center justify-content-between"
                                data-test="sort-by-category"
                            >
                                Category
                                <i :class="getSortIconClass('category')"></i>
                            </button>
                        </th>
                        <th>
                            <button
                                @click.prevent="sortBy('online_price')"
                                class="d-flex align-content-center align-items-center justify-content-between"
                                data-test="sort-by-online-price"
                            >
                                Online Base Price
                                <i :class="getSortIconClass('online_price')"></i>
                            </button>
                        </th>
                        <th>
                            <button
                                @click.prevent="sortBy('total_asset_value')"
                                class="d-flex align-content-center align-items-center justify-content-between"
                                data-test="sort-by-total-value"
                            >
                                Total Asset Value
                                <i :class="getSortIconClass('total_asset_value')"></i>
                            </button>
                        </th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="asset in assets" :key="`assetRow${asset.id}`" :data-test="`asset-row-${asset.id}`">
                        <td class="checkbox-column">
                            <div class="custom-control custom-checkbox">
                                <input
                                    v-model="selectedAssets"
                                    :value="asset.id"
                                    type="checkbox"
                                    name="selected"
                                    class="custom-control-input"
                                    :id="`tableSelect${asset.id}`"
                                    :data-test="`asset-checkbox-${asset.id}`"
                                />
                                <label class="custom-control-label" :for="`tableSelect${asset.id}`"></label>
                            </div>
                        </td>
                        <td>
                            <a
                                :href="getEditUrl(asset)"
                                target="_blank"
                                class="text-decoration-none"
                                :data-test="`asset-name-link-${asset.id}`"
                            >
                                {{ asset.name }}
                            </a>
                        </td>
                        <td>
                            <div class="text-left" :data-test="`asset-category-${asset.id}`">
                                {{ asset.category.name }}
                            </div>
                        </td>
                        <td>
                            <div class="text-center" :data-test="`asset-online-price-${asset.id}`">
                                {{ asset.online_price | money }}
                            </div>
                        </td>
                        <td>
                            <div class="text-center" :data-test="`asset-total-value-${asset.id}`">
                                {{ asset.total_asset_value | money }}
                            </div>
                        </td>
                        <td>
                            <div class="dropdown text-center">
                                <a href="#" class="p2 text-dark" data-toggle="dropdown" :data-test="`asset-actions-dropdown-${asset.id}`">
                                    <i class="fal fa-2x fa-ellipsis-v"></i>
                                </a>
                                <div class="dropdown-menu">
                                    <a :href="getEditUrl(asset)" class="dropdown-item" target="_blank" :data-test="`edit-asset-${asset.id}`">
                                        Edit Asset
                                    </a>
                                    <a
                                        href="#"
                                        @click.prevent="confirmRemoveAsset(asset.id)"
                                        class="dropdown-item"
                                        :data-test="`remove-asset-${asset.id}`"
                                    >
                                        Remove Asset
                                    </a>
                                    <a
                                        :href="`/team/assets/${asset.id}`"
                                        class="dropdown-item"
                                        target="_blank"
                                        :data-test="`preview-asset-${asset.id}`"
                                    >
                                        Preview Asset
                                    </a>
                                </div>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>

                <!-- Empty state -->
                <div v-if="assets.length === 0" class="text-center p-4">
                    No assets found ready to post.
                </div>
            </div>
            <small>
                * Team/Venue is responsible for all posted inventory. Gameday Gateway takes no liability in what is
                posted and sold by a Team/Venue. Team is fully liable to execute all requirements of
                listing once sold.
            </small>
        </div>

        <div class="col-4">
            <!-- Email dropdown -->
            <div class="dropdown">
                <button
                    :disabled="selectedAssets.length === 0"
                    class="btn btn-secondary btn-md btn-divided mb-4 w-100 dropdown-toggle"
                    type="button"
                    id="emailAssetsButton"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                    data-test="email-dropdown-button"
                >
                    Email Ready to Post Summary
                </button>
                <div class="dropdown-menu p-3 w-100" aria-labelledby="emailAssetsButton" data-test="email-dropdown-menu">
                    <form @submit.prevent="sendEmailSummary">
                        <div class="form-group">
                            <input
                                v-model="emailForm.email"
                                type="email"
                                class="form-control"
                                placeholder="Email"
                                required
                                data-test="email-input"
                            />
                        </div>
                        <button
                            type="submit"
                            class="btn btn-primary btn-sm"
                            :disabled="isEmailSending || !emailForm.email"
                            data-test="send-email-button"
                        >
              <span v-if="isEmailSending">
                <i class="fas fa-spinner fa-spin"></i> Sending...
              </span>
                            <span v-else>Send Email</span>
                        </button>
                    </form>
                </div>
            </div>

            <!-- Summary card -->
            <div id="previewSummary" class="card mt-2" data-test="summary-card">
                <div class="card-body">
                    <h3>Summary</h3>
                    <div class="form-group row d-flex align-items-center">
                        <label class="col-12 col-md-7 col-form-label">Total Value of Ready to Post Assets:</label>
                        <div class="col-12 col-md-5 align-middle" data-test="total-value-all">
                            {{ totalValue | money }}
                        </div>
                    </div>
                    <div class="card-divider mb-3"></div>
                    <p data-test="selected-count">
                        You have selected
                        <strong>{{ selectedAssets.length }} {{
                                selectedAssets.length === 1 ? 'asset' : 'assets'
                            }}</strong>
                        to post.
                    </p>
                    <p data-test="selected-total-value">Total Value: <strong>{{ selectedTotalValue | money }}</strong></p>
                </div>
            </div>

            <!-- Post button -->
            <button
                :disabled="selectedAssets.length === 0 || isPosting"
                class="btn btn-secondary btn-dark w-100"
                @click="postAssets"
                data-test="post-assets-button"
            >
        <span v-if="isPosting">
          <i class="fas fa-spinner fa-spin"></i> Posting...
        </span>
                <span v-else>Post Selected Assets</span>
            </button>
        </div>

        <!-- Partnership Agreement Modal placeholder -->
        <div id="partnership-agreement-modal"></div>
    </div>
</template>

<script>
import { money } from '@jgile/laravel-vue/resources/js/util';
import axios from 'axios';
import _ from 'lodash';

export default {
    name: 'PreviewAndPost',
    props: {
        stagedIds: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            assets: [],
            selectedAssets: [],
            selectAll: false,
            isLoading: false,
            isPosting: false,
            isEmailSending: false,
            searchName: '',
            sortField: 'name',
            sortDirection: 'asc',
            emailForm: {
                email: '',
            },
            error: null,
        };
    },
    computed: {
        totalValue() {
            return this.assets.reduce((sum, asset) => sum + (asset.total_asset_value || 0), 0);
        },
        selectedTotalValue() {
            const selected = this.assets.filter(asset => this.selectedAssets.includes(asset.id));
            return selected.reduce((sum, asset) => sum + (asset.total_asset_value || 0), 0);
        },
    },
    created() {
        this.debouncedSearch = _.debounce(this.fetchAssets, 300);
    },
    mounted() {
        this.fetchAssets();

        // Listen for partnership agreement modal events
        window.Bus.$on('showPartnershipAgreement', this.showPartnershipAgreement);
        window.Bus.$on('assetsPosted', this.handleAssetsPosted);
    },
    beforeDestroy() {
        window.Bus.$off('showPartnershipAgreement', this.showPartnershipAgreement);
        window.Bus.$off('assetsPosted', this.handleAssetsPosted);
    },
    watch: {
        selectedAssets: {
            handler(newVal) {
                this.selectAll = newVal.length === this.assets.length && this.assets.length > 0;

                // Update email form with selected asset IDs
                this.emailForm.asset_ids = [...newVal];
            },
            deep: true,
        },
    },
    methods: {
        async fetchAssets() {
            this.isLoading = true;
            this.error = null;

            try {
                const params = new URLSearchParams();

                if (this.searchName.trim()) {
                    params.append('filter[name]', this.searchName.trim());
                }

                if (this.sortField) {
                    const sortParam = this.sortDirection === 'desc' ? `-${this.sortField}` : this.sortField;
                    params.append('sort', sortParam);
                }

                const response = await axios.get(`/api/team/preview-assets?${params.toString()}`);

                this.assets = response.data.data || [];

                // Auto-select all assets by default
                this.selectedAssets = this.assets.map(asset => asset.id);
                this.selectAll = this.assets.length > 0;

            } catch (error) {
                console.error('Error fetching preview assets:', error);
                this.error = 'Failed to load assets. Please try again.';
                this.assets = [];
            } finally {
                this.isLoading = false;
            }
        },

        toggleSelectAll() {
            if (this.selectAll) {
                this.selectedAssets = this.assets.map(asset => asset.id);
            } else {
                this.selectedAssets = [];
            }
        },

        sortBy(field) {
            if (this.sortField === field) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortField = field;
                this.sortDirection = 'asc';
            }
            this.fetchAssets();
        },

        getSortIconClass(field) {
            if (this.sortField !== field) {
                return 'fas fa-sort text-muted';
            }
            return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
        },

        getEditUrl(asset) {
            const isSimple = asset.asset_type === 'simple';
            return isSimple
                ? `/team/assets/${asset.id}/edit`
                : `/team/bundles/${asset.id}/details`;
        },

        async confirmRemoveAsset(assetId) {
            const result = await swal({
                title: 'Are you sure?',
                text: 'This will remove the asset from the ready to post list.',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, remove it!',
                confirmButtonClass: 'btn btn-danger mr-2',
                cancelButtonClass: 'btn btn-cancel',
                buttonsStyling: false,
            });

            if (result.value) {
                await this.removeAsset(assetId);
            }
        },

        async removeAsset(assetId) {
            try {
                await axios.put(`/api/team/preview-assets/${assetId}`);

                // Remove from local state
                this.assets = this.assets.filter(asset => asset.id !== assetId);
                this.selectedAssets = this.selectedAssets.filter(id => id !== assetId);

                // Show success message
                this.$messenger.success('Asset removed from ready to post.');

            } catch (error) {
                console.error('Error removing asset:', error);
                this.$messenger.error('Failed to remove asset. Please try again.');
            }
        },

        async sendEmailSummary() {
            if (!this.emailForm.email || this.selectedAssets.length === 0) {
                return;
            }

            this.isEmailSending = true;

            try {
                await axios.post('/api/team/preview-assets/mail', {
                    email: this.emailForm.email,
                    asset_ids: this.selectedAssets,
                });

                this.$messenger.success('Email sent successfully!');
                this.emailForm.email = '';

                // Close dropdown
                $('#emailAssetsButton').dropdown('hide');

            } catch (error) {
                console.error('Error sending email:', error);
                this.$messenger.error('Failed to send email. Please try again.');
            } finally {
                this.isEmailSending = false;
            }
        },

        async postAssets() {
            if (this.selectedAssets.length === 0) {
                return;
            }

            this.isPosting = true;

            try {
                await axios.post('/api/team/preview-assets', {
                    asset_ids: this.selectedAssets,
                    partnership_agreement_at: new Date().toISOString(),
                });

                // Emit success event for handling by parent or global listener
                window.Bus.$emit('assetsPosted');

            } catch (error) {
                console.error('Error posting assets:', error);

                if (error.response?.status === 422) {
                    // Handle partnership agreement requirement
                    window.Bus.$emit('showPartnershipAgreement');
                } else {
                    this.$messenger.error('Failed to post assets. Please try again.');
                }
            } finally {
                this.isPosting = false;
            }
        },

        showPartnershipAgreement() {
            // This would show the partnership agreement modal
            // Implementation would depend on how modals are handled in the app
            console.log('Show partnership agreement modal');
        },

        handleAssetsPosted() {
            swal({
                title: 'Success',
                html: 'The assets have been posted.',
                type: 'success',
                confirmButtonClass: 'btn btn-danger mr-2',
                cancelButtonClass: 'btn btn-cancel',
                buttonsStyling: false,
                showCancelButton: true,
                confirmButtonText: 'Posted Assets',
                cancelButtonText: 'Inventory Manager',
            }).then((result) => {
                if (result.value) {
                    window.location = '/team/inventory/posted';
                } else {
                    window.location = '/team/inventory';
                }
            });
        },

        formatMoney(amount) {
            if (amount == null) return '$0.00';

            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }).format(amount);
        },
    },
};
</script>

<style scoped>
.checkbox-column {
    width: 50px;
}

.card-gg {
    /* Preserve existing gameday gateway card styling */
}

.table-a {
    /* Preserve existing table styling */
}

.btn-divided {
    /* Preserve existing button styling */
}

.card-divider {
    /* Preserve existing divider styling */
    border-bottom: 1px solid #dee2e6;
}
</style>
